import React, { useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import treemap from 'highcharts/modules/treemap';
import { chartStyle } from '@components/charts/config';
import Loading from '@components/loading';
import D_Number from '@utils/number';
import { deepCopy } from '@utils/common';
import highchartsmore from 'highcharts/highcharts-more';

highchartsmore(Highcharts);
treemap(Highcharts);

const Colors = [
  '#855F00',
  '#A37400',
  '#C28A00',
  '#E0A000',
  '#FFB600',
  '#FFBF1F',
  '#FFC83D',
  '#FFDC85',
  '#FFECBD',
];

const TreeMapOption = {
  chart: {
    margin: [5, 0, 0, 0],
  },
  tooltip: {
    enabled: true,
    formatter() {
      return `<div class="le-tooltip">
        <div class="le-title" style='background-color:#FFB600; font-size: 0.875rem;'>
          ${this.point.name}
        </div>
        <br/>
        <b style="width:12.5rem">${this.point.formattedValue}</b>
      </div>
    `;
    },
  },
  credits: { enabled: false },
  subtitle: {
    text: undefined,
  },
  title: {
    text: undefined,
  },
  series: [
    {
      layoutStartingDirection: 'horizontal',
      name: 'Country',
      type: 'treemap',
      layoutAlgorithm: 'squarified',
      allowTraversingTree: true,
      animationLimit: 1000,
      opacity: 1,
      dataLabels: {
        enabled: true,
        align: 'left',
        verticalAlign: 'top',
        crop: false,
        padding: 0,
        overflow: 'allow',
        style: {
          textOverflow: 'ellipsis',
          fontSize: '6px',
          fontWeight: '500',
          color: 'white',
        },
        filter: {
          property: 'percentage',
          operator: '>',
          value: 1,
        },
        formatter() {
          return `<div class="treemap-data-label" style='color:${
            this.point.fontColor || 'white'
          };font-size:0.625rem;padding:0.313rem'>
          <div style="fill:${this.point.fontColor || 'white'}">${
            this.point.name
          }</div>
          <br/>
          <div class="label-amount" style="font-weight:550;fill:${
            this.point.fontColor || 'white'
          }">${this.point.formattedValue}</div>
        </div>
        `;
        },
        // useHTML: true,
      },
      zoneAxis: 'x',
      levels: [
        {
          level: 1,
          borderWidth: 1,
          borderColor: '#DBDBDB',
          levelIsConstant: true,
        },
        {
          level: 1,
          levelIsConstant: false,
          dataLabels: {
            enabled: true,
            style: {
              color: 'white',
              fontSize: '0.75rem',
              fontFamily: 'Helvetica Neue,Arial',
              fontWeight: 500,
            },
          },
        },
      ],
      accessibility: {
        exposeAsGroupOnly: true,
      },
      data: [],
    },
  ],
};

const formatValueByField = (data, field) => {
  if (field === 'headcount') {
    return D_Number.formatWithoutSuffix(data.headcount);
  }
  return `$${D_Number.format(data.sales)}`;
};
const getTotal = (arr, field) =>
  arr.reduce((acc, e) => acc + (e[field] || 0), 0);

const formatPointData = (dataToFormat, field, color, total, fontColor) => ({
  ...dataToFormat,
  color,
  id: dataToFormat.companyName,
  name: dataToFormat.companyName,
  value: dataToFormat[field],
  formattedValue: formatValueByField(dataToFormat, field),
  percentage: (total === 0 ? 0 : dataToFormat[field] / total) * 100,
  fontColor,
});

const flatAndFormatData = (arr, field) => {
  const total = getTotal(arr, field);

  return arr
    .sort((a, b) => b[field] - a[field])
    .reduce((acc, e, i) => {
      const { country, [field]: value } = e;

      return [
        ...acc,
        {
          country,
          id: country,
          name: country,
          value,
          color: Colors[i % 9],
          formattedValue: formatValueByField(e, field),
          percentage: (total === 0 ? 0 : value / total) * 100,
          fontColor: i % 9 > 3 ? '#2d2d2d' : 'white',
        },
      ];
    }, []);
};

const getTreeOption = ({ onClickCountry }) => {
  const option = deepCopy(TreeMapOption);
  option.series[0].events = { click: onClickCountry };
  return option;
};

const filterDataBySelectedCountries = (data, selectedCountries) =>
  data.filter((elem) => selectedCountries.indexOf(elem.country) !== -1);

const HeadCountAndSalesTreeMap = ({
  currentData,
  data,
  expand,
  type,
  focusedCountry,
  setFocusedCountry,
  selectedCountry = [],
}) => {
  const [mapData, setMapData] = useState([]);
  const [loading, setIsLoading] = useState(false);
  const chartRef = useRef(null);

  const onClickCountry = (e) => {
    if (focusedCountry) {
      return;
    }
    setFocusedCountry(e?.point?.country || null);
  };

  const updateSeriesData = (option, data) => {
    option.series[0].data = data;
    // option.series[0].levels[0].borderWidth = focusedCountry ? 1 : 0;
    return option;
  };

  const options = updateSeriesData(getTreeOption({ onClickCountry }), mapData);

  useEffect(() => {
    const filteredData = filterDataBySelectedCountries(
      currentData,
      selectedCountry,
    );
    if (!focusedCountry) {
      setMapData(flatAndFormatData(filteredData, type));
      return;
    }
    const countryDataIndex = (filteredData || []).findIndex(
      (c) => c.country === focusedCountry,
    );

    const countryData = filteredData[countryDataIndex];
    if (!countryData) {
      return;
    }
    const companyList =
      type === 'sales'
        ? countryData.companySalesList
        : countryData.companyHeadcountList;

    const total = getTotal(companyList, type);
    setIsLoading(true);
    const newMapData = companyList.map((elem) =>
      formatPointData(
        elem,
        type,
        Colors[countryDataIndex % 9],
        total,
        countryDataIndex % 9 > 3 ? '#2d2d2d' : 'white',
      ),
    );

    setMapData(newMapData);
    setTimeout(() => setIsLoading(false), 100);
  }, [focusedCountry, selectedCountry, currentData, type, expand]);

  useEffect(() => {
    setFocusedCountry(null);
  }, [currentData, type]);

  useEffect(() => {
    try {
      if (!chartRef || !chartRef.current) {
        return;
      }
      const { chart } = chartRef.current;
      if (chart?.reflow) {
        chart.reflow();
        chart.redraw();
      }
    } catch (error) {
      console.error(error);
    }
  }, [expand]);

  const isMapDataApplicable = mapData.some((e) => e.value !== null);

  if (!isMapDataApplicable) {
    return (
      <div className="highchart-treemap-wrapper not-applicable-placeholder">
        Not Applicable
      </div>
    );
  }

  return (
    <div className={`highchart-treemap-wrapper ${expand ? 'expanded' : ''}`}>
      <div className="treemap-indicator">
        <div style={{ display: focusedCountry ? 'flex' : 'none' }}>
          <span onClick={() => setFocusedCountry(null)}>Country</span>
          <span>/</span>
          <span>{focusedCountry}</span>
        </div>
      </div>
      {loading ? (
        <Loading isLoading={true} className="treemap-loading" />
      ) : (
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          constructorType="chart"
          options={options}
          containerProps={{
            style: chartStyle,
          }}
        />
      )}
    </div>
  );
};

export default HeadCountAndSalesTreeMap;
